import { getSiteName } from '@provision/formatting-utils';
import { useMemo, useState } from 'react';
import { AddSiteStep } from 'src/components/AddSiteModal/add-site-step';
import {
  ModalContainer,
  ModalList,
  type ModalListItem,
  useModalSteps,
} from 'src/components/ModalContainer';
import type { SelectMenuCreateAction } from 'src/components/SelectMenu/select-menu.types';
import { Address } from 'src/graphql/entities/address/address';
import { DEFAULT_PAGINATION_LIMIT } from 'src/graphql/entities/search/use-search';
import { ConditionInput } from 'src/graphql/types';

const SITE_SEARCH_DEBOUNCE_MS = 500;

type SelectSiteListProps = {
  testId: string;
  loading?: boolean;
  filters: ConditionInput[];
  options: ModalListItem[];
  partnerId?: number | null;
  onSearchChange: (value: string) => void;
  /** `createdSite` is set when the row is a site created from this picker. */
  onSelect?: (item: ModalListItem, createdSite?: Address) => void;
};

type SelectSiteDialogProps = SelectSiteListProps & {
  open: boolean;
  title: string;
  onClose: () => void;
};

const SelectSiteList = ({
  testId,
  loading,
  filters,
  options,
  partnerId,
  onSearchChange,
  onSelect,
}: SelectSiteListProps) => {
  const { pushStep } = useModalSteps();
  const [createdSites, setCreatedSites] = useState<Address[]>([]);
  const [highlightedId, setHighlightedId] = useState<string>();

  const items = useMemo(
    () => [
      ...createdSites
        .filter((site) => !options.some((option) => option.id === site.addressId))
        .map((site) => ({ id: site.addressId, title: getSiteName(site) })),
      ...options,
    ],
    [createdSites, options],
  );

  const createSite = useMemo<SelectMenuCreateAction>(() => {
    const openCreateSiteStep = async () => {
      const site = await pushStep<Address>(
        <AddSiteStep
          partnerId={partnerId}
          cacheOptions={{ filters, limit: DEFAULT_PAGINATION_LIMIT * 10 }}
        />,
      );
      if (site) {
        setCreatedSites((current) => [site, ...current]);
        setHighlightedId(site.addressId);
      }
    };

    return { label: 'Create site', onSelect: () => void openCreateSiteStep() };
  }, [filters, partnerId, pushStep]);

  return (
    <ModalList
      items={items}
      onSelect={(item) =>
        onSelect?.(
          item,
          createdSites.find((site) => site.addressId === item.id),
        )
      }
      loading={loading}
      shouldFilter={false}
      onSearchChange={onSearchChange}
      searchDebounce={SITE_SEARCH_DEBOUNCE_MS}
      placeholder="Type to find and associate site…"
      createAction={createSite}
      highlightedId={highlightedId}
      onHighlightChange={setHighlightedId}
      testId={testId}
    />
  );
};

export const SelectSiteDialog = ({
  open,
  title,
  onClose,
  ...listProps
}: SelectSiteDialogProps) => (
  <ModalContainer
    open={open}
    size="medium"
    title={title}
    onClose={onClose}
    data-testid={listProps.testId}
  >
    <SelectSiteList {...listProps} />
  </ModalContainer>
);
